import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { checkLoginStatus } from '../../../redux/WithcookieSlice';
import { fetchUnilevelTree, fetchUnilevelTreeNodeChildren } from '../../../redux/UnilevelTreeActions';
import { useTranslation } from '../../../redux/TranslationHelper';
import { showToast } from '../../common/Toast';
import { OrganizationChartNodeData, UnilevelLegsLogicReturn } from './UnilevelLegs.types';

// 전역 중복 방지 맵
const globalFetchMap = new Map<string, boolean>();

export const useUnilevelLegsLogic = (componentProps: any = {}): UnilevelLegsLogicReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as any;
  
  // Redux state
  const user = useSelector((state: any) => state.withcookie.user);
  const unilevelTreeData = useSelector((state: any) => state.withcookie.unilevelTreeData);
  const unilevelTreeLoading = useSelector((state: any) => state.withcookie.unilevelTreeLoading);
  const unilevelTreeError = useSelector((state: any) => state.withcookie.unilevelTreeError);
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  
  // Theme colors
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#3f51b5";
  const secondaryColor = colorSet?.secondary || "#e91e63";
  
  // State
  const [currentUserId, setCurrentUserId] = useState<string | number | null>(null);
  const [debugMode, setDebugMode] = useState<boolean>(
    typeof componentProps.debugMode !== 'undefined' ? componentProps.debugMode : false
  );
  const [mockingEnabled, setMockingEnabled] = useState<boolean>(false);
  const [customApiUrl, setCustomApiUrl] = useState<string>('');
  const [nodeHistory, setNodeHistory] = useState<Array<string | number>>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(componentProps.initialZoom || 1);
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [loadingNodes, setLoadingNodes] = useState<Set<string | number>>(new Set());
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPos = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const lastTouchDist = useRef<number | null>(null);
  const fetchInProgressRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const mountedRef = useRef(true);
  
  const minZoom = componentProps.minZoom || 0.3;
  const maxZoom = componentProps.maxZoom || 2.5;
  
  // 컴포넌트 언마운트 감지
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      fetchInProgressRef.current = false;
    };
  }, []);
  
  // 초기 데이터 로드
  useEffect(() => {
    const currentParams = JSON.stringify({ type: 'checkLoginStatus' });
    const globalKey = `unilevel-legs-auth-${currentParams}`;
    
    if (globalFetchMap.get(globalKey) || fetchInProgressRef.current || lastFetchParamsRef.current === currentParams) {
      return;
    }
    
    globalFetchMap.set(globalKey, true);
    fetchInProgressRef.current = true;
    lastFetchParamsRef.current = currentParams;
    
    const checkAuth = async () => {
      try {
        await dispatch(checkLoginStatus({ forceRefresh: true }));
      } finally {
        if (mountedRef.current) {
          fetchInProgressRef.current = false;
        }
        globalFetchMap.delete(globalKey);
      }
    };
    
    checkAuth();
  }, [dispatch]);
  
  // Mock data generator
  const generateMockData = useCallback((rootUserId: string | number): OrganizationChartNodeData => {
    const rootId = rootUserId || (user?.id || 1);
    return {
      id: rootId,
      name: `사용자 ${rootId}`,
      userId: `user${rootId}`,
      class: 'shipper',
      backgroundColor: '#6D81F8',
      totalPV: 500,
      rank: '다이아몬드',
      grade: 'VIP',
      phone: '010-1234-5678',
      center: '강남센터',
      groupPv: 15000,
      underMore: true,
      children: [
        {
          id: Number(rootId) + 1,
          name: `사용자 ${Number(rootId) + 1}`,
          userId: `user${Number(rootId) + 1}`,
          backgroundColor: '#BAF855',
          totalPV: 1000,
          rank: '플래티넘',
          grade: '일반',
          phone: '010-2345-6789',
          center: '강북센터',
          groupPv: 8000,
          underMore: true,
          children: []
        },
        {
          id: Number(rootId) + 2,
          name: `사용자 ${Number(rootId) + 2}`,
          userId: `user${Number(rootId) + 2}`,
          backgroundColor: '#FF6B6B',
          totalPV: 750,
          rank: '골드',
          grade: '일반',
          phone: '010-3456-7890',
          center: '분당센터',
          groupPv: 5000,
          underMore: false,
          children: []
        },
        {
          id: Number(rootId) + 3,
          name: `사용자 ${Number(rootId) + 3}`,
          userId: `user${Number(rootId) + 3}`,
          backgroundColor: '#4ECDC4',
          totalPV: 600,
          rank: '실버',
          grade: '일반',
          phone: '010-4567-8901',
          center: '일산센터',
          groupPv: 3000,
          underMore: true,
          children: []
        }
      ]
    };
  }, [user]);
  
  // Center chart
  const centerChart = useCallback(() => {
    if (!containerRef.current || hasUserInteracted) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const chartRect = orgChart.getBoundingClientRect();
    const chartWidth = chartRect.width;
    const chartHeight = chartRect.height;
    
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    let offsetY;
    if (chartHeight > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
    
    setIsInitialLoad(false);
  }, [hasUserInteracted]);
  
  // Manual center chart
  const manualCenterChart = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const chartRect = orgChart.getBoundingClientRect();
    const chartWidth = chartRect.width;
    const chartHeight = chartRect.height;
    
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    let offsetY;
    if (chartHeight > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
  }, []);
  
  // Zoom at point
  const zoomAtPoint = useCallback((newZoom: number, clientX: number, clientY: number) => {
    if (!containerRef.current || newZoom === zoomLevel) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    
    const scale = newZoom / zoomLevel;
    
    const newOffsetX = mouseX - (mouseX - offset.x) * scale;
    const newOffsetY = mouseY - (mouseY - offset.y) * scale;
    
    setZoomLevel(newZoom);
    setOffset({
      x: newOffsetX,
      y: newOffsetY
    });
  }, [zoomLevel, offset]);
  
  // Node click handler
  const handleNodeClick = useCallback((nodeData: OrganizationChartNodeData, isRootNode = false) => {
    if (String(currentUserId) === String(nodeData.id) && !isRootNode) {
      return;
    }
    
    if (currentUserId !== null && String(currentUserId) !== String(nodeData.id)) {
      setNodeHistory(prev => [...prev, currentUserId]);
    }
    
    if (String(currentUserId) !== String(nodeData.id)) {
      setCurrentUserId(nodeData.id);
    }
    
    const apiParams = {
      id: String(nodeData.id),
      depth: "6"
    };
    
    const isTopLevelNode = isRootNode || 
                           (unilevelTreeData && String(unilevelTreeData.id) === String(nodeData.id)) ||
                           !unilevelTreeData;
    
    if (isTopLevelNode || isRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    dispatch(fetchUnilevelTree({...apiParams})).unwrap().catch((error: any) => {
      showToast(error || '데이터를 불러오는 중 오류가 발생했습니다.', 'error');
    });
  }, [dispatch, currentUserId, unilevelTreeData]);
  
  // More button click handler
  const handleMoreButtonClick = useCallback(async (nodeId: string | number) => {
    setLoadingNodes(prev => new Set(prev).add(nodeId));
    
    try {
      await dispatch(fetchUnilevelTreeNodeChildren({
        id: String(nodeId),
        depth: "8"
      })).unwrap();
    } catch (error) {
      console.error('[유니레벨 레그] 추가 데이터 로드 오류:', error);
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }
  }, [dispatch]);
  
  // Navigation handlers
  const goToPreviousNode = useCallback(() => {
    if (!nodeHistory || nodeHistory.length === 0) {
      return;
    }
    
    const prevNodeId = nodeHistory[nodeHistory.length - 1];
    setNodeHistory(nodeHistory.slice(0, -1));
    setCurrentUserId(prevNodeId);
    
    const isUserRootNode = user && user.id && String(prevNodeId) === String(user.id);
    
    const apiParams = {
      id: String(prevNodeId),
      depth: "8"
    };
    
    if (isUserRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    dispatch(fetchUnilevelTree({...apiParams})).unwrap().catch((error: any) => {
      showToast(error || '데이터를 불러오는 중 오류가 발생했습니다.', 'error');
    });
  }, [nodeHistory, dispatch, user]);
  
  const goToRootNode = useCallback(() => {
    if (!user || !user.id) {
      return;
    }
    
    if (String(currentUserId) === String(user.id)) {
      return;
    }
    
    setNodeHistory([]);
    setCurrentUserId(user.id);
    
    const apiParams = {
      id: String(user.id),
      depth: "8",
      one_upper: true
    };
    
    dispatch(fetchUnilevelTree({...apiParams})).unwrap().catch((error: any) => {
      showToast(error || '데이터를 불러오는 중 오류가 발생했습니다.', 'error');
    });
  }, [user, dispatch, currentUserId]);
  
  // Debug mode handlers
  const toggleDebugMode = useCallback(() => {
    setDebugMode(!debugMode);
  }, [debugMode]);
  
  const enableMockData = useCallback(() => {
    setMockingEnabled(true);
  }, []);
  
  const retryWithRealApi = useCallback(() => {
    if (user && user.id) {
      setMockingEnabled(false);
      dispatch(fetchUnilevelTree({ id: String(user.id) })).unwrap().catch((error: any) => {
        showToast(error || '데이터를 불러오는 중 오류가 발생했습니다.', 'error');
      });
    }
  }, [user, dispatch]);
  
  // Mouse event handlers
  const handleWheel = useCallback((e: React.WheelEvent) => {
    setHasUserInteracted(true);
    e.preventDefault();
    e.stopPropagation();
    
    const zoomSpeed = 0.15;
    let delta = e.deltaY < 0 ? zoomSpeed : -zoomSpeed;
    
    if (e.ctrlKey) {
      delta *= 0.5;
    }
    
    const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel + delta));
    
    if (newZoom !== zoomLevel) {
      zoomAtPoint(newZoom, e.clientX, e.clientY);
    }
  }, [zoomLevel, zoomAtPoint, minZoom, maxZoom]);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setHasUserInteracted(true);
    
    if (e.button === 0) {
      setIsDragging(true);
      lastPos.current = { x: e.clientX, y: e.clientY };
      
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grabbing';
      }
      
      e.preventDefault();
    }
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    
    setOffset(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging]);
  
  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  }, [isDragging]);
  
  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setHasUserInteracted(true);
    e.preventDefault();
    
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
      
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      lastPos.current = { x: centerX, y: centerY };
      
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 2 && lastTouchDist.current) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      
      if (Math.abs(newDist - lastTouchDist.current) > 5) {
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        const scaleFactor = newDist / lastTouchDist.current;
        const clampedScale = Math.max(0.9, Math.min(1.1, scaleFactor));
        const newZoom = Math.min(maxZoom, Math.max(minZoom, zoomLevel * clampedScale));
        
        if (newZoom !== zoomLevel) {
          zoomAtPoint(newZoom, centerX, centerY);
        }
        
        lastTouchDist.current = newDist;
      }
    } else if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - lastPos.current.x;
      const dy = e.touches[0].clientY - lastPos.current.y;
      
      setOffset(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [isDragging, zoomLevel, zoomAtPoint, minZoom, maxZoom]);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    lastTouchDist.current = null;
    
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  }, []);
  
  // Reset zoom
  const resetZoom = useCallback(() => {
    setZoomLevel(componentProps.initialZoom || 1);
    setHasUserInteracted(false);
    setTimeout(() => {
      manualCenterChart();
    }, 50);
  }, [manualCenterChart, componentProps.initialZoom]);
  
  // Load initial data when user is available
  useEffect(() => {
    if (user && !unilevelTreeData && !unilevelTreeLoading) {
      if (currentUserId === null) {
        setCurrentUserId(user.id);
        setNodeHistory([]);
        
        dispatch(fetchUnilevelTree({ 
          id: String(user.id),
          depth: "8"
        })).unwrap().catch((error: any) => {
          showToast(error || '데이터를 불러오는 중 오류가 발생했습니다.', 'error');
        });
      }
    }
  }, [user, dispatch, unilevelTreeData, unilevelTreeLoading, currentUserId]);
  
  // Center chart when data loads
  useEffect(() => {
    if (unilevelTreeData && !unilevelTreeLoading && isInitialLoad && !hasUserInteracted) {
      const timer = setTimeout(() => {
        centerChart();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [unilevelTreeData, unilevelTreeLoading, isInitialLoad, hasUserInteracted, centerChart]);
  
  // Reset interaction state when node changes
  useEffect(() => {
    if (currentUserId !== null && unilevelTreeData && !unilevelTreeLoading) {
      setHasUserInteracted(false);
      setIsInitialLoad(true);
    }
  }, [currentUserId, unilevelTreeData, unilevelTreeLoading]);
  
  // Enable mock data when toggled
  useEffect(() => {
    if (mockingEnabled && user) {
      generateMockData(user.id);
    }
  }, [mockingEnabled, user, generateMockData]);
  
  return {
    // State
    currentUserId,
    debugMode,
    mockingEnabled,
    customApiUrl,
    nodeHistory,
    zoomLevel,
    offset,
    isDragging,
    hasUserInteracted,
    isInitialLoad,
    loadingNodes,
    
    // Redux state
    user,
    unilevelTreeData,
    unilevelTreeLoading,
    unilevelTreeError,
    withcookieData,
    primaryColor,
    secondaryColor,
    
    // Refs
    containerRef,
    lastPos,
    lastTouchDist,
    
    // Actions
    handleNodeClick,
    handleMoreButtonClick,
    goToPreviousNode,
    goToRootNode,
    toggleDebugMode,
    enableMockData,
    retryWithRealApi,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    manualCenterChart,
    resetZoom,
    setDebugMode,
    setMockingEnabled,
    setCustomApiUrl,
    
    // Utils
    t,
    generateMockData
  };
};